import React from 'react';
import { Link } from 'react-router-dom';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import './privacyPolicy.css';

const PrivacyPolicy = () => {
    return (
        <Box className="privacy-policy-page">
            <Container maxWidth="md" className="privacy-policy-container">
                <Paper elevation={3} className="privacy-policy-paper">
                    <Box className="privacy-policy-header">
                        <Link to="/gsa" className="back-link">
                            <IconButton aria-label="back to home" className="back-button">
                                <ArrowBackIcon />
                            </IconButton>
                        </Link>
                        <img 
                            src="assets/images/logo-completo.png" 
                            alt="GSA - Global Scuba Association Logo" 
                            className="privacy-logo"
                        />
                    </Box>

                    <Typography variant="h3" component="h1" className="privacy-title">
                        Privacy Policy
                    </Typography>
                    
                    <Typography variant="body2" className="privacy-updated">
                        Last updated: February 24, 2026
                    </Typography>

                    <Divider className="privacy-divider" />

                    <Box className="privacy-content">
                        <Typography variant="body1" paragraph>
                            Welcome to <strong>GSA - Global Scuba Association</strong> (the "App"), operated by <strong>Global Scuba Association LLC</strong> ("we," "our," or "us").
                        </Typography>

                        <Typography variant="body1" paragraph>
                            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application designed for scuba diving instructors, dive professionals, and certified divers.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            By using the App, you agree to the collection and use of information in accordance with this Privacy Policy.
                        </Typography>

                        <Divider className="section-divider" />

                        {/* Section 1 */}
                        <Typography variant="h5" component="h2" className="section-title">
                            1. Information We Collect
                        </Typography>

                        <Typography variant="body1" paragraph>
                            We may collect the following types of information:
                        </Typography>

                        <Typography variant="h6" component="h3" className="subsection-title">
                            a) Personal Information
                        </Typography>

                        <Typography variant="body1" paragraph>
                            When you register or use the App, we may collect:
                        </Typography>

                        <ul className="privacy-list">
                            <li>Full name</li>
                            <li>Email address</li>
                            <li>Date of birth</li>
                            <li>Profile photo</li>
                            <li>Mailing address (street, city, province, zip code, country)</li>
                            <li>Instructor certification details (for instructor accounts)</li>
                            <li>Diver certification details (for diver accounts)</li>
                        </ul>

                        <Typography variant="h6" component="h3" className="subsection-title">
                            b) Professional & Diving Data
                        </Typography>

                        <Typography variant="body1" paragraph>
                            To provide instructor and diver-related features:
                        </Typography>

                        <ul className="privacy-list">
                            <li>Student certifications issued</li>
                            <li>Dive logs (date, location, depth, duration, water temperature, visibility, dive type, buddy name, notes)</li>
                            <li>Dive photos uploaded to logbook entries</li>
                            <li>Training records</li>
                            <li>Course participation data</li>
                            <li>Dive site information</li>
                            <li>Trivia game progress and scores</li>
                        </ul>

                        <Typography variant="h6" component="h3" className="subsection-title">
                            c) Location Data
                        </Typography>

                        <Typography variant="body1" paragraph>
                            We may collect location data provided by the user when logging dive sites. Location data is only collected when explicitly entered by the user in dive log entries.
                        </Typography>

                        <Typography variant="h6" component="h3" className="subsection-title">
                            d) Device & Usage Data
                        </Typography>

                        <Typography variant="body1" paragraph>
                            We automatically collect:
                        </Typography>

                        <ul className="privacy-list">
                            <li>Device type & operating system</li>
                            <li>App version</li>
                            <li>IP address</li>
                            <li>Crash logs</li>
                            <li>Usage analytics</li>
                        </ul>

                        <Divider className="section-divider" />

                        {/* Section 2 */}
                        <Typography variant="h5" component="h2" className="section-title">
                            2. How We Use Your Information
                        </Typography>

                        <Typography variant="body1" paragraph>
                            We use collected data to:
                        </Typography>

                        <ul className="privacy-list">
                            <li>Provide and maintain the App</li>
                            <li>Manage instructor and diver accounts</li>
                            <li>Facilitate instructor-diver linking and communication</li>
                            <li>Manage and display diving certifications</li>
                            <li>Track dive training and logbook entries</li>
                            <li>Share educational materials between instructors and divers</li>
                            <li>Provide trivia and gamification features</li>
                            <li>Process payments for certifications and memberships</li>
                            <li>Improve safety and training quality</li>
                            <li>Monitor and analyze usage to improve the App</li>
                            <li>Detect fraud or misuse</li>
                            <li>Comply with legal obligations</li>
                        </ul>

                        <Divider className="section-divider" />

                        {/* Section 3 */}
                        <Typography variant="h5" component="h2" className="section-title">
                            3. Sharing of Information
                        </Typography>

                        <Typography variant="body1" paragraph>
                            We may share information with:
                        </Typography>

                        <Typography variant="h6" component="h3" className="subsection-title">
                            a) Instructors and Divers
                        </Typography>

                        <Typography variant="body1" paragraph>
                            When a diver links with an instructor, certain profile information (name, email, photo, certifications) is shared between both parties to facilitate the training relationship.
                        </Typography>

                        <Typography variant="h6" component="h3" className="subsection-title">
                            b) Diving Organizations
                        </Typography>

                        <Typography variant="body1" paragraph>
                            When required for certification validation or training standards compliance.
                        </Typography>

                        <Typography variant="h6" component="h3" className="subsection-title">
                            c) Service Providers
                        </Typography>

                        <Typography variant="body1" paragraph>
                            Third parties that help operate the App, such as:
                        </Typography>

                        <ul className="privacy-list">
                            <li><strong>Supabase</strong> — Database hosting, authentication, and file storage</li>
                            <li><strong>Stripe</strong> — Payment processing for certifications and memberships</li>
                            <li><strong>Expo / React Native</strong> — App framework and distribution</li>
                            <li><strong>Apple App Store / Google Play Store</strong> — App distribution</li>
                        </ul>

                        <Typography variant="body1" paragraph>
                            These providers only access data necessary to perform their services.
                        </Typography>

                        <Typography variant="h6" component="h3" className="subsection-title">
                            d) Legal Requirements
                        </Typography>

                        <Typography variant="body1" paragraph>
                            We may disclose data if required to:
                        </Typography>

                        <ul className="privacy-list">
                            <li>Comply with laws or regulations</li>
                            <li>Respond to legal requests</li>
                            <li>Protect rights and safety of our users</li>
                        </ul>

                        <Divider className="section-divider" />

                        {/* Section 4 */}
                        <Typography variant="h5" component="h2" className="section-title">
                            4. Data Storage & Security
                        </Typography>

                        <Typography variant="body1" paragraph>
                            We implement appropriate technical and organizational security measures, including:
                        </Typography>

                        <ul className="privacy-list">
                            <li>Encrypted data transmission (HTTPS/TLS)</li>
                            <li>Secure cloud storage via Supabase with Row Level Security (RLS)</li>
                            <li>Authentication via secure token-based sessions</li>
                            <li>Access controls ensuring users can only access their own data</li>
                        </ul>

                        <Typography variant="body1" paragraph>
                            However, no method of transmission or storage is 100% secure. We cannot guarantee absolute security.
                        </Typography>

                        <Divider className="section-divider" />

                        {/* Section 5 */}
                        <Typography variant="h5" component="h2" className="section-title">
                            5. Data Retention
                        </Typography>

                        <Typography variant="body1" paragraph>
                            We retain personal data only as long as necessary to:
                        </Typography>

                        <ul className="privacy-list">
                            <li>Provide our services</li>
                            <li>Maintain certification records (which may be required indefinitely for verification purposes)</li>
                            <li>Comply with legal obligations</li>
                        </ul>

                        <Typography variant="body1" paragraph>
                            You may request deletion of your data at any time (see Section 8).
                        </Typography>

                        <Divider className="section-divider" />

                        {/* Section 6 */}
                        <Typography variant="h5" component="h2" className="section-title">
                            6. Children's Privacy
                        </Typography>

                        <Typography variant="body1" paragraph>
                            The App is not intended for children under 13 (or the equivalent minimum age in your jurisdiction).
                        </Typography>

                        <Typography variant="body1" paragraph>
                            We do not knowingly collect personal data from children without parental consent. If you believe we have inadvertently collected such data, please contact us immediately.
                        </Typography>

                        <Divider className="section-divider" />

                        {/* Section 7 */}
                        <Typography variant="h5" component="h2" className="section-title">
                            7. International Data Transfers
                        </Typography>

                        <Typography variant="body1" paragraph>
                            If you access the App from outside the United States, your data may be transferred internationally and processed under applicable data protection laws. By using the App, you consent to such transfers.
                        </Typography>

                        <Divider className="section-divider" />

                        {/* Section 8 */}
                        <Typography variant="h5" component="h2" className="section-title">
                            8. Your Privacy Rights
                        </Typography>

                        <Typography variant="body1" paragraph>
                            Depending on your jurisdiction, you may have the right to:
                        </Typography>

                        <ul className="privacy-list">
                            <li><strong>Access</strong> your personal data</li>
                            <li><strong>Correct</strong> inaccurate data</li>
                            <li><strong>Request deletion</strong> of your data</li>
                            <li><strong>Restrict processing</strong> of your data</li>
                            <li><strong>Export</strong> your data in a portable format</li>
                            <li><strong>Withdraw consent</strong> at any time</li>
                        </ul>

                        <Typography variant="body1" paragraph>
                            To exercise these rights, contact us at:
                        </Typography>

                        <Typography variant="body1" paragraph className="contact-email">
                            <a href="mailto:support@divegsa.com">support@divegsa.com</a>
                        </Typography>

                        <Typography variant="body1" paragraph>
                            We will respond to your request within 30 days.
                        </Typography>

                        <Divider className="section-divider" />

                        {/* Section 9 */}
                        <Typography variant="h5" component="h2" className="section-title">
                            9. Third-Party Services
                        </Typography>

                        <Typography variant="body1" paragraph>
                            The App may use third-party services, each with their own privacy policies:
                        </Typography>

                        <ul className="privacy-list third-party-links">
                            <li><strong>Supabase</strong> — <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer">https://supabase.com/privacy</a></li>
                            <li><strong>Stripe</strong> — <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">https://stripe.com/privacy</a></li>
                            <li><strong>Expo</strong> — <a href="https://expo.dev/privacy" target="_blank" rel="noopener noreferrer">https://expo.dev/privacy</a></li>
                            <li><strong>Apple</strong> — <a href="https://www.apple.com/privacy" target="_blank" rel="noopener noreferrer">https://www.apple.com/privacy</a></li>
                            <li><strong>Google</strong> — <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a></li>
                        </ul>

                        <Divider className="section-divider" />

                        {/* Section 10 */}
                        <Typography variant="h5" component="h2" className="section-title">
                            10. Changes to This Privacy Policy
                        </Typography>

                        <Typography variant="body1" paragraph>
                            We may update this Privacy Policy from time to time.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            We will notify users by:
                        </Typography>

                        <ul className="privacy-list">
                            <li>Updating the "Last updated" date at the top of this document</li>
                            <li>Posting a notice inside the App when significant changes are made</li>
                        </ul>

                        <Typography variant="body1" paragraph>
                            Continued use of the App after changes constitutes acceptance of the updated policy.
                        </Typography>

                        <Divider className="section-divider" />

                        {/* Section 11 */}
                        <Typography variant="h5" component="h2" className="section-title">
                            11. Contact Us
                        </Typography>

                        <Typography variant="body1" paragraph>
                            If you have questions or concerns about this Privacy Policy:
                        </Typography>

                        <Box className="contact-info">
                            <Typography variant="body1">
                                <strong>Email:</strong> <a href="mailto:support@divegsa.com">support@divegsa.com</a>
                            </Typography>
                            <Typography variant="body1">
                                <strong>Website:</strong> <a href="https://webapp.divegsa.com" target="_blank" rel="noopener noreferrer">https://webapp.divegsa.com</a>
                            </Typography>
                            <Typography variant="body1">
                                <strong>Organization:</strong> Global Scuba Association LLC
                            </Typography>
                            <Typography variant="body1">
                                <strong>Address:</strong> 7901 4th St N, STE 300, St. Petersburg, FL, USA
                            </Typography>
                        </Box>

                        <Divider className="section-divider" />

                        <Typography variant="body2" className="privacy-footer">
                            <em>End of Privacy Policy</em>
                        </Typography>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default PrivacyPolicy;
